import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/categories";

const navLinks = [
  { label: "首页", to: "/" },
  { label: "搜索", to: "/search" },
  { label: "关于我们", to: "/about" },
];

export default function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    setMobileOpen(false);
    setCategoryOpen(false);
  }, [location.pathname]);

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    if (value) {
      navigate(`/search?q=${encodeURIComponent(value)}`);
    } else {
      navigate("/search");
    }
  };

  const isActive = (to: string) => {
    if (to === "/") return location.pathname === "/";
    return location.pathname.startsWith(to.replace("/search", "/search"));
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="flex h-16 w-full items-center gap-4 px-4 md:px-8">
        <Link to="/" className="flex shrink-0 items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-plant-line text-xl"></i>
          </span>
          {location.pathname === "/" ? (
            <h1 className="font-heading text-lg font-bold tracking-tight text-foreground-950">
              黄瓜视频下载
            </h1>
          ) : (
            <span className="font-heading text-lg font-bold tracking-tight text-foreground-950">
              黄瓜视频下载
            </span>
          )}
        </Link>

        <nav className="ml-4 hidden items-center gap-1 lg:flex">
          <Link
            to="/"
            className={`whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              isActive("/")
                ? "text-primary-600"
                : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
            }`}
          >
            首页
          </Link>
          <div
            className="relative"
            onMouseEnter={() => setCategoryOpen(true)}
            onMouseLeave={() => setCategoryOpen(false)}
          >
            <button
              type="button"
              onClick={() => setCategoryOpen((prev) => !prev)}
              className="flex items-center gap-1 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 hover:text-foreground-950"
            >
              分类
              <i className={`ri-arrow-down-s-line transition-transform ${categoryOpen ? "rotate-180" : ""}`}></i>
            </button>
            {categoryOpen ? (
              <div className="absolute left-0 top-full w-[420px] pt-2">
                <div className="grid grid-cols-2 gap-1 rounded-lg border border-background-200 bg-background-50 p-2">
                  {categories.map((category) => (
                    <Link
                      key={category.id}
                      to={`/category/${category.id}`}
                      className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-700"
                    >
                      <i className={`${category.icon} text-base text-primary-500`}></i>
                      {category.name}
                    </Link>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
          {navLinks.slice(1).map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                location.pathname.startsWith(link.to)
                  ? "text-primary-600"
                  : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <form
          onSubmit={submitSearch}
          className="ml-auto hidden items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-1.5 md:flex"
        >
          <i className="ri-search-line text-base text-foreground-500"></i>
          <input
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            type="text"
            name="q"
            placeholder="搜索视频标题或标签"
            className="w-40 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400 lg:w-56"
          />
          <button
            type="submit"
            className="whitespace-nowrap rounded-md bg-primary-500 px-3 py-1 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            搜索
          </button>
        </form>

        <button
          type="button"
          onClick={() => setMobileOpen((prev) => !prev)}
          aria-label="打开菜单"
          className="ml-auto flex h-10 w-10 items-center justify-center rounded-md text-foreground-800 transition-colors hover:bg-background-100 lg:hidden"
        >
          <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl`}></i>
        </button>
      </div>

      {mobileOpen ? (
        <div className="border-t border-background-200 bg-background-50 px-4 py-4 lg:hidden">
          <form
            onSubmit={submitSearch}
            className="mb-4 flex items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2"
          >
            <i className="ri-search-line text-base text-foreground-500"></i>
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              type="text"
              name="q"
              placeholder="搜索视频标题或标签"
              className="flex-1 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
            <button
              type="submit"
              className="whitespace-nowrap rounded-md bg-primary-500 px-3 py-1 text-xs font-medium text-background-50"
            >
              搜索
            </button>
          </form>
          <div className="flex flex-col gap-1">
            <Link
              to="/"
              className="rounded-md px-3 py-2 text-sm font-medium text-foreground-800 hover:bg-background-100"
            >
              首页
            </Link>
            <Link
              to="/search"
              className="rounded-md px-3 py-2 text-sm font-medium text-foreground-800 hover:bg-background-100"
            >
              搜索
            </Link>
            <Link
              to="/about"
              className="rounded-md px-3 py-2 text-sm font-medium text-foreground-800 hover:bg-background-100"
            >
              关于我们
            </Link>
          </div>
          <p className="mt-4 mb-2 px-3 text-xs font-semibold text-foreground-500">视频分类</p>
          <div className="grid grid-cols-2 gap-1">
            {categories.map((category) => (
              <Link
                key={category.id}
                to={`/category/${category.id}`}
                className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700"
              >
                <i className={`${category.icon} text-base text-primary-500`}></i>
                {category.name}
              </Link>
            ))}
          </div>
        </div>
      ) : null}
    </header>
  );
}